import React, { useState, useEffect } from 'react'
import { X, Wand2, Image as ImageIcon, Save, Shirt, Upload, Coins, RefreshCw, ImagePlus } from 'lucide-react'
import clothingService from '../services/clothingService.js'
import playCreateStorageService from '../services/playCreateStorageService.js'
import toast from 'react-hot-toast'

// 点数消耗配置
const POINTS_CONFIG = {
  SINGLE_IMAGE_COST: 10,    // 单图换装消耗
  DUAL_IMAGE_COST: 15,      // 双图融合消耗
}

export const ClothingDressingRoom = ({ character, onClose, onClothingGenerated, isEmbedded = false }) => {
  // 基本状态
  const [clothingName, setClothingName] = useState('')
  const [clothingDescription, setClothingDescription] = useState('')
  const [clothingCategory, setClothingCategory] = useState('full')
  
  // 模式和生成状态
  const [mode, setMode] = useState('single') // 'single' 或 'dual'
  const [isGenerating, setIsGenerating] = useState(false)
  const [generatedImage, setGeneratedImage] = useState(null)
  const [imageLoadError, setImageLoadError] = useState(false)
  
  // 参考图
  const [referenceClothingImage, setReferenceClothingImage] = useState(null)
  const [referenceClothingPreview, setReferenceClothingPreview] = useState(null)
  
  // 服装列表和选中的参考服装
  const [clothingList, setClothingList] = useState([])
  const [selectedReferenceClothing, setSelectedReferenceClothing] = useState(null) // null 表示使用默认立绘
  
  // 上传的参考图（角色参考图）
  const [uploadedReferenceImage, setUploadedReferenceImage] = useState(null)
  const [uploadedReferencePreview, setUploadedReferencePreview] = useState(null)
  
  // 点数
  const [currentPoints, setCurrentPoints] = useState(0)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  
  // 加载点数和服装列表
  useEffect(() => {
    const loadData = async () => {
      if (character?.id) {
        const points = await playCreateStorageService.getCharacterPoints(character.id)
        setCurrentPoints(points)
        
        // 加载服装列表
        const clothing = await clothingService.getCharacterClothing(character.id)
        setClothingList(clothing)
      }
    }
    loadData()
  }, [character])
  
  // 获取当前模式需要的点数
  const getRequiredPoints = () => {
    return mode === 'single' ? POINTS_CONFIG.SINGLE_IMAGE_COST : POINTS_CONFIG.DUAL_IMAGE_COST
  }
  
  // 检查点数是否足够
  const hasEnoughPoints = () => {
    return currentPoints >= getRequiredPoints()
  }
  
  // 获取当前参考图URL（优先级：上传图 > 选中服装 > 默认立绘）
  const getCurrentReferenceImageUrl = () => {
    // 1. 优先使用上传的参考图
    if (uploadedReferencePreview) {
      return uploadedReferencePreview
    }
    
    // 2. 使用选中的服装作为参考
    if (selectedReferenceClothing) {
      if (selectedReferenceClothing.imageBlob) {
        return URL.createObjectURL(selectedReferenceClothing.imageBlob)
      }
      return selectedReferenceClothing.generatedImageUrl || selectedReferenceClothing.imageUrl
    }
    
    // 3. 使用默认立绘
    return character?.originalAvatar || character?.avatar
  }
  
  // 获取当前参考图名称
  const getCurrentReferenceName = () => {
    if (uploadedReferencePreview) {
      return '上传的参考图'
    }
    if (selectedReferenceClothing) {
      return selectedReferenceClothing.name
    }
    return '默认立绘'
  }
  
  // 处理参考服装切换
  const handleReferenceClothingChange = (clothing) => {
    setSelectedReferenceClothing(clothing)
    // 切换服装时清除上传的图片
    setUploadedReferenceImage(null)
    setUploadedReferencePreview(null)
    
    if (clothing) {
      toast.success(`已切换到：${clothing.name}`)
    } else {
      toast.success('已切换到默认立绘')
    }
  }
  
  // 处理角色参考图上传
  const handleReferenceImageUpload = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    // 验证文件类型
    if (!file.type.startsWith('image/')) {
      toast.error('请上传图片文件')
      return
    }
    
    // 验证文件大小（10MB）
    if (file.size > 10 * 1024 * 1024) {
      toast.error('图片大小不能超过10MB')
      return
    }
    
    // 创建预览
    const reader = new FileReader()
    reader.onload = (event) => {
      const dataUrl = event.target.result
      setUploadedReferenceImage(dataUrl)
      setUploadedReferencePreview(dataUrl)
      // 清除选中的服装
      setSelectedReferenceClothing(null)
      toast.success('参考图上传成功')
    }
    reader.readAsDataURL(file)
  }
  
  // 清除上传的参考图
  const handleClearUploadedReference = () => {
    setUploadedReferenceImage(null)
    setUploadedReferencePreview(null)
    toast.success('已清除上传的参考图')
  }
  
  // 处理服装参考图上传（双图模式）
  const handleReferenceUpload = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    // 验证文件类型
    if (!file.type.startsWith('image/')) {
      toast.error('请上传图片文件')
      return
    }
    
    // 验证文件大小（10MB）
    if (file.size > 10 * 1024 * 1024) {
      toast.error('图片大小不能超过10MB')
      return
    }
    
    // 创建预览
    const reader = new FileReader()
    reader.onload = (event) => {
      const dataUrl = event.target.result
      setReferenceClothingImage(dataUrl)
      setReferenceClothingPreview(dataUrl)
      toast.success('服装参考图上传成功')
    }
    reader.readAsDataURL(file)
  }
  
  // 生成换装立绘（带点数确认）
  const handleGenerate = async () => {
    if (!clothingDescription.trim()) {
      toast.error('请输入服装描述')
      return
    }
    
    // 双图模式需要参考图
    if (mode === 'dual' && !referenceClothingImage) {
      toast.error('双图模式需要上传服装参考图')
      return
    }
    
    // 检查点数
    if (!hasEnoughPoints()) {
      toast.error(`点数不足！需要 ${getRequiredPoints()} 点，当前 ${currentPoints} 点`)
      return
    }
    
    // 显示确认对话框
    setShowConfirmDialog(true)
  }
  
  // 确认生成
  const handleConfirmGenerate = async () => {
    setShowConfirmDialog(false)
    
    try {
      setIsGenerating(true)
      const requiredPoints = getRequiredPoints()
      const modeName = mode === 'single' ? '单图参考换装' : '双图融合换装'
      
      toast.loading(`正在准备生成（${modeName}）...`, { id: 'generating-clothing' })
      
      // 消耗点数
      const pointsResult = await playCreateStorageService.consumePoints(
        character.id,
        requiredPoints,
        `${modeName}生成`
      )
      
      if (!pointsResult.success) {
        toast.error(pointsResult.message, { id: 'generating-clothing' })
        return
      }
      
      // 更新显示的点数
      setCurrentPoints(pointsResult.currentPoints)
      
      // 准备服装信息
      const clothing = {
        name: clothingName.trim() || '新服装',
        description: clothingDescription.trim(),
        category: clothingCategory,
        character: character
      }
      
      // 更新提示：调用API
      toast.loading(
        `🎨 正在生成换装立绘，预计需要1-5分钟，请耐心等待...`, 
        { id: 'generating-clothing', duration: 600000 } // 10分钟
      )
      
      // 调用图生图API
      const imageUrl = await clothingService.generateClothedAvatarWithReference(
        character,
        clothing,
        {
          mode: mode,
          referenceClothingImage: mode === 'dual' ? referenceClothingImage : null,
          selectedReferenceClothing: selectedReferenceClothing, // 传入选中的参考服装
          uploadedReferenceImage: uploadedReferenceImage // 传入上传的参考图
        }
      )
      
      setGeneratedImage(imageUrl)
      setImageLoadError(false)
      
      toast.success(`立绘生成成功！消耗 ${requiredPoints} 点，剩余 ${pointsResult.currentPoints} 点`, { 
        id: 'generating-clothing',
        duration: 4000
      })
    } catch (error) {
      console.error('❌ 生成换装立绘失败:', error)
      
      // 生成失败，退还点数
      const requiredPoints = getRequiredPoints()
      await playCreateStorageService.addPoints(
        character.id,
        requiredPoints,
        '生成失败退还'
      )
      
      // 更新点数显示
      const points = await playCreateStorageService.getCharacterPoints(character.id)
      setCurrentPoints(points)
      
      // 根据错误类型给出不同的提示
      let errorMessage = error.message || '未知错误'
      
      if (errorMessage.includes('超时')) {
        errorMessage = '⏱️ 生成超时，可能因为服务器繁忙。已退还点数，请稍后重试。'
      } else if (errorMessage.includes('网络')) {
        errorMessage = '🌐 网络连接失败，已退还点数。请检查网络后重试。'
      } else if (errorMessage.includes('内容审核') || errorMessage.includes('sensitive')) {
        errorMessage = '⚠️ 内容审核未通过。已退还点数。请检查：\n' +
                      '1. 参考图是否包含敏感内容\n' +
                      '2. 服装描述是否包含敏感词汇\n' +
                      '3. 建议使用健康、正能量的描述（如：传统服饰、职业装等）'
      } else {
        errorMessage = `❌ 生成失败：${errorMessage}。已退还点数。`
      }
      
      toast.error(errorMessage, { 
        id: 'generating-clothing',
        duration: 6000 
      })
    } finally {
      setIsGenerating(false)
    }
  }
  
  // 保存到服装库
  const handleSave = async () => {
    if (!generatedImage) {
      toast.error('请先生成立绘')
      return
    }
    
    try {
      const clothing = {
        name: clothingName.trim() || '新服装',
        description: clothingDescription.trim(),
        category: clothingCategory,
        character: character
      }
      
      await clothingService.saveClothingToLibrary(character.id, clothing, generatedImage)
      toast.success('已保存到服装库！')
      
      if (onClothingGenerated) {
        onClothingGenerated()
      }
      
      onClose()
    } catch (error) {
      console.error('❌ 保存失败:', error)
      const errorMessage = error.message || error.toString()
      if (errorMessage.includes('数据库版本需要升级') || 
          errorMessage.includes('object stores was not found') ||
          errorMessage.includes('characterClothing')) {
        toast.error('数据库需要升级，请按 Ctrl+F5 (或 Cmd+Shift+R) 强制刷新页面', {
          duration: 8000,
          icon: '⚠️'
        })
        setTimeout(() => {
          window.location.reload()
        }, 3000)
      } else {
        toast.error('保存失败：' + errorMessage)
      }
    }
  }
  
  // 嵌入模式：不显示外层弹窗和头部
  const content = (
    <div className={isEmbedded ? "h-full overflow-y-auto" : ""}>
      <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 左侧：参考图预览 */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">参考图</h3>
            {/* 点数显示 */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-lg border border-yellow-500/30">
              <Coins size={18} className="text-yellow-400" />
              <span className="text-yellow-400 font-bold">{currentPoints}</span>
              <span className="text-yellow-400/70 text-sm">点</span>
            </div>
          </div>
          
          {/* 角色参考图（可切换/上传） */}
          <div className="bg-dark-700 rounded-lg p-3 border border-dark-600">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm text-dark-300">角色参考图</div>
              <div className="flex items-center gap-1">
                {/* 上传按钮 */}
                <label className="flex items-center gap-1 px-2 py-1 text-xs bg-primary/80 hover:bg-primary text-white rounded transition-colors cursor-pointer">
                  <Upload size={12} />
                  上传
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleReferenceImageUpload}
                    className="hidden"
                  />
                </label>
                
                {/* 切换按钮 */}
                {clothingList.length > 0 && !uploadedReferencePreview && (
                  <button
                    onClick={() => {
                      // 切换到下一个服装或返回默认
                      const currentIndex = selectedReferenceClothing 
                        ? clothingList.findIndex(c => c.id === selectedReferenceClothing.id)
                        : -1
                      const nextIndex = (currentIndex + 1) % (clothingList.length + 1)
                      const nextClothing = nextIndex === clothingList.length ? null : clothingList[nextIndex]
                      handleReferenceClothingChange(nextClothing)
                    }}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-dark-600 hover:bg-dark-500 text-white rounded transition-colors"
                    title="切换参考图"
                  >
                    <RefreshCw size={12} />
                    切换
                  </button>
                )}
                
                {/* 清除上传按钮 */}
                {uploadedReferencePreview && (
                  <button
                    onClick={handleClearUploadedReference}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-red-500/80 hover:bg-red-500 text-white rounded transition-colors"
                    title="清除上传的图片"
                  >
                    <X size={12} />
                    清除
                  </button>
                )}
              </div>
            </div>
            
            {/* 显示当前选中的参考图 */}
            <div className="relative">
              {getCurrentReferenceImageUrl() ? (
                <img
                  src={getCurrentReferenceImageUrl()}
                  alt="参考立绘"
                  className="w-full rounded-lg border border-dark-500"
                />
              ) : (
                <div className="aspect-square bg-dark-600 rounded-lg flex items-center justify-center">
                  <ImageIcon size={32} className="text-dark-400" />
                </div>
              )}
              
              {/* 显示当前参考图名称 */}
              <div className="absolute bottom-2 left-2 right-2 bg-black/70 backdrop-blur-sm rounded px-2 py-1">
                <div className="text-xs text-white truncate flex items-center gap-1">
                  {uploadedReferencePreview && <Upload size={10} className="flex-shrink-0" />}
                  {getCurrentReferenceName()}
                </div>
              </div>
            </div>
            
            {/* 服装选择下拉菜单（上传图片时隐藏） */}
            {clothingList.length > 0 && !uploadedReferencePreview && (
              <div className="mt-2">
                <select
                  value={selectedReferenceClothing?.id || 'default'}
                  onChange={(e) => {
                    const clothing = e.target.value === 'default' 
                      ? null 
                      : clothingList.find(c => c.id === e.target.value)
                    handleReferenceClothingChange(clothing)
                  }}
                  className="w-full px-3 py-2 bg-dark-600 text-white text-sm rounded border border-dark-500 focus:border-primary focus:outline-none"
                >
                  <option value="default">默认立绘</option>
                  {clothingList.map((clothing) => (
                    <option key={clothing.id} value={clothing.id}>
                      {clothing.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {clothingList.length === 0 && (
              <div className="mt-2 text-xs text-dark-400 text-center">
                还没有其他服装可供切换
              </div>
            )}
          </div>
          
          {/* 双图模式：服装参考图 */}
            {mode === 'dual' && (
              <div className="bg-dark-700 rounded-lg p-3 border border-dark-600">
                <div className="text-sm text-dark-300 mb-2">服装参考图</div>
                {referenceClothingPreview ? (
                  <div className="relative">
                    <img
                      src={referenceClothingPreview}
                      alt="服装参考"
                      className="w-full rounded-lg border border-dark-500"
                    />
                    <button
                      onClick={() => {
                        setReferenceClothingImage(null)
                        setReferenceClothingPreview(null)
                      }}
                      className="absolute top-2 right-2 p-1 bg-red-500/80 hover:bg-red-500 rounded text-white"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <label className="aspect-square bg-dark-600 rounded-lg flex flex-col items-center justify-center border-2 border-dashed border-dark-500 hover:border-primary/50 cursor-pointer transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleReferenceUpload}
                      className="hidden"
                    />
                    <Upload size={32} className="text-dark-400 mb-2" />
                    <span className="text-sm text-dark-400">上传服装参考图</span>
                  </label>
                )}
              </div>
            )}
          </div>
          
          {/* 中间：输入区域 */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">服装设置</h3>
            
            {/* 模式切换 */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                生成模式
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setMode('single')}
                  className={`px-4 py-2 rounded-lg border transition-all ${
                    mode === 'single'
                      ? 'bg-primary border-primary text-white'
                      : 'bg-dark-700 border-dark-600 text-dark-300 hover:border-primary/50'
                  }`}
                >
                  <div className="text-sm font-medium">单图参考</div>
                  <div className="text-xs opacity-70">{POINTS_CONFIG.SINGLE_IMAGE_COST} 点</div>
                </button>
                <button
                  onClick={() => setMode('dual')}
                  className={`px-4 py-2 rounded-lg border transition-all ${
                    mode === 'dual'
                      ? 'bg-primary border-primary text-white'
                      : 'bg-dark-700 border-dark-600 text-dark-300 hover:border-primary/50'
                  }`}
                >
                  <div className="text-sm font-medium">双图融合</div>
                  <div className="text-xs opacity-70">{POINTS_CONFIG.DUAL_IMAGE_COST} 点</div>
                </button>
              </div>
              <div className="mt-2 text-xs text-dark-400">
                {mode === 'single' 
                  ? '使用角色原始立绘作为参考，只修改服装'
                  : '融合角色立绘和服装参考图，实现精确换装'
                }
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                服装名称
              </label>
              <input
                type="text"
                value={clothingName}
                onChange={(e) => setClothingName(e.target.value)}
                placeholder="例如：红色汉服"
                className="w-full bg-dark-700 border border-dark-600 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                服装描述 *
              </label>
              <textarea
                value={clothingDescription}
                onChange={(e) => setClothingDescription(e.target.value)}
                placeholder={
                  mode === 'single'
                    ? '描述要更换的服装，例如：红色传统汉服，飘逸长袖，金色刺绣'
                    : '描述如何融合服装风格，例如：将角色服装替换为参考图的汉服风格'
                }
                rows={4}
                className="w-full bg-dark-700 border border-dark-600 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
              <div className="mt-2 text-xs text-dark-400 bg-dark-700/50 rounded px-3 py-2 border border-dark-600">
                <div className="flex items-start gap-2">
                  <span className="text-yellow-500">💡</span>
                  <div>
                    <div className="font-medium text-yellow-500 mb-1">内容提示</div>
                    <ul className="space-y-1">
                      <li>• 请使用健康、正能量的描述</li>
                      <li>• 避免使用敏感词汇（如：暴露、透明等）</li>
                      <li>• 推荐：传统服饰、时尚服装、职业装等</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                服装类别
              </label>
              <select
                value={clothingCategory}
                onChange={(e) => setClothingCategory(e.target.value)}
                className="w-full bg-dark-700 border border-dark-600 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="top">上装</option>
                <option value="bottom">下装</option>
                <option value="dress">连衣裙</option>
                <option value="accessory">配饰</option>
                <option value="full">全身套装</option>
              </select>
            </div>
            
            {/* 生成按钮 */}
            <button
              onClick={handleGenerate}
              disabled={isGenerating || !clothingDescription.trim() || !hasEnoughPoints()}
              className="w-full px-4 py-3 bg-gradient-to-r from-primary to-secondary hover:from-primary/80 hover:to-secondary/80 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium shadow-lg"
            >
              {isGenerating ? (
                <>
                  <RefreshCw size={18} className="animate-spin" />
                  生成中...
                </>
              ) : (
                <>
                  <Wand2 size={18} />
                  生成换装（消耗 {getRequiredPoints()} 点）
                </>
              )}
            </button>
            
            {!hasEnoughPoints() && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-400">
                  ⚠️ 点数不足！需要 {getRequiredPoints()} 点，当前 {currentPoints} 点
                </p>
              </div>
          )}
        </div>
        
        {/* 右侧：预览区域 */}
        <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">生成结果</h3>
            
            <div className="bg-dark-700 rounded-lg p-4 border border-dark-600">
              {generatedImage ? (
                <div className="space-y-4">
                  <div className="relative">
                    <img
                      src={generatedImage}
                      alt="换装预览"
                      className="w-full rounded-lg border-2 border-dark-600"
                      onError={() => setImageLoadError(true)}
                      onLoad={() => setImageLoadError(false)}
                    />
                    {imageLoadError && (
                      <div className="absolute inset-0 bg-dark-700/90 rounded-lg flex items-center justify-center">
                        <div className="text-center text-white p-4">
                          <ImageIcon size={48} className="mx-auto mb-2 opacity-50" />
                          <p className="text-sm mb-2">图片加载受限</p>
                          <p className="text-xs text-dark-300">但仍可以保存到服装库</p>
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handleSave}
                    className="w-full px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-lg transition-all font-medium flex items-center justify-center gap-2 shadow-lg"
                  >
                    <Save size={18} />
                    保存到服装库
                  </button>
                </div>
              ) : (
                <div className="aspect-square bg-dark-600 rounded-lg flex items-center justify-center border-2 border-dashed border-dark-500">
                  <div className="text-center text-dark-400">
                    <ImagePlus size={48} className="mx-auto mb-2" />
                    <p>生成后将在这里显示</p>
                  </div>
                </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )

  // 如果是嵌入模式，直接返回内容
  if (isEmbedded) {
    return (
      <>
        {content}
        {/* 点数消耗确认对话框 */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-dark-800 rounded-lg p-6 w-full max-w-md border border-dark-600">
            <h3 className="text-xl font-bold text-white mb-4">确认生成</h3>
            <div className="space-y-3 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-dark-300">生成模式：</span>
                <span className="text-white font-medium">
                  {mode === 'single' ? '单图参考' : '双图融合'}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-dark-300">消耗点数：</span>
                <span className="text-yellow-400 font-bold">{getRequiredPoints()} 点</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-dark-300">剩余点数：</span>
                <span className="text-white">{currentPoints - getRequiredPoints()} 点</span>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="flex-1 px-4 py-2 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleConfirmGenerate}
                className="flex-1 px-4 py-2 bg-primary hover:bg-primary/80 text-white rounded-lg transition-colors font-medium"
              >
                确认生成
              </button>
            </div>
          </div>
        </div>
      )}
      </>
    )
  }

  // 独立模式：包含弹窗容器
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800/30 backdrop-blur-md rounded-lg w-full max-w-6xl max-h-[90vh] overflow-y-auto border border-dark-600/30 shadow-2xl">
        {/* 头部 */}
        <div className="flex items-center justify-between p-6 border-b border-dark-600/30 sticky top-0 bg-dark-800/20 backdrop-blur-md z-10">
          <div className="flex items-center gap-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Shirt size={24} className="text-primary" />
              {character?.name}的衣柜
            </h2>
            {/* 点数显示 */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-lg border border-yellow-500/30">
              <Coins size={18} className="text-yellow-400" />
              <span className="text-yellow-400 font-bold">{currentPoints}</span>
              <span className="text-yellow-400/70 text-sm">点</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-dark-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        {content}
        {/* 点数消耗确认对话框 */}
        {showConfirmDialog && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
            <div className="bg-dark-800 rounded-lg p-6 w-full max-w-md border border-dark-600">
              <h3 className="text-xl font-bold text-white mb-4">确认生成</h3>
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-dark-300">生成模式：</span>
                  <span className="text-white font-medium">
                    {mode === 'single' ? '单图参考' : '双图融合'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-dark-300">消耗点数：</span>
                  <span className="text-yellow-400 font-bold">{getRequiredPoints()} 点</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-dark-300">剩余点数：</span>
                  <span className="text-white">{currentPoints - getRequiredPoints()} 点</span>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmDialog(false)}
                  className="flex-1 px-4 py-2 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleConfirmGenerate}
                  className="flex-1 px-4 py-2 bg-primary hover:bg-primary/80 text-white rounded-lg transition-colors font-medium"
                >
                  确认生成
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
